import React, { useEffect, useState } from 'react';
import { Coins, Wallet, Save, Copy, Check, TrendingUp, Cpu, Waves, GitBranch } from 'lucide-react';
import API from '../lib/api';
import { useToast } from '../hooks/use-toast';

const PROJECT_ICONS = {
  ai_robot: Cpu,
  skyway_string: GitBranch,
  skyway_bridge: Waves,
};

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DividendTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [addr, setAddr] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get('/dividends/me');
      setData(data);
      setAddr(data.wallet_bep20 || '');
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const saveAddr = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post('/dividends/me/wallet', { address: addr });
      toast({ title: 'Wallet saved', description: 'Dividends will be paid to this USDT BEP20 address.' });
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    if (!addr) return;
    navigator.clipboard?.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const items = data?.items || [];

  return (
    <div className="space-y-6">
      {/* Header + Projects */}
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <Coins size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Dividend Programme</span>
        </div>
        <p className="mt-1 text-[12.5px] text-emerald-900/65">Track project-level dividend payouts. Distribution is done by SkywayGreen admin based on your total invested amount.</p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {(data?.projects || []).map((p) => {
            const Icon = PROJECT_ICONS[p.id] || Coins;
            const active = p.status === 'ACTIVE';
            return (
              <div
                key={p.id}
                data-testid={`project-${p.id}`}
                className={`rounded-xl border p-4 ${active ? 'border-emerald-500/60 bg-emerald-50' : 'border-emerald-900/15 bg-white/60'}`}
              >
                <div className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] px-2 py-1 rounded ${active ? 'bg-emerald-700 text-emerald-50' : 'bg-amber-100 text-amber-800'}`}>
                  {active ? 'Paid dividend' : 'Coming soon'}
                </div>
                <div className="mt-3 flex items-center gap-2 text-emerald-900">
                  <Icon size={18} className="text-emerald-700" />
                  <div className="font-serif text-[15px] leading-tight">{p.name}</div>
                </div>
                {active && (
                  <div className="mt-3 text-[11px] text-emerald-800/70">Distributions triggered manually by admin based on % of investment.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard label="Total Investment" value={money(data?.total_investment)} sub="Sum of your approved packages" />
        <StatCard label="Received Dividend" value={money(data?.total_dividend_received)} sub={`${money(data?.total_dividend_pending)} pending payout`} highlight />
      </div>

      {/* BEP20 wallet save */}
      <form onSubmit={saveAddr} className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <Wallet size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Dividend Payout Wallet · USDT BEP20</span>
        </div>
        <p className="mt-1 text-[12.5px] text-emerald-900/65">Save your Binance Smart Chain (BEP20) wallet address. Admin will send dividend USDT to this address only.</p>

        <div className="mt-4 flex flex-col md:flex-row gap-2">
          <input
            data-testid="dividend-wallet-input"
            value={addr}
            onChange={(e) => setAddr(e.target.value)}
            placeholder="0x… (BEP20 / BSC address)"
            className="portal-input flex-1 font-mono text-[13px]"
            required
            minLength={20}
          />
          <button
            data-testid="dividend-wallet-copy"
            type="button"
            onClick={copy}
            disabled={!addr}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-100 text-emerald-900 hover:bg-emerald-200 disabled:opacity-40 px-4 py-2 text-sm font-semibold transition"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            data-testid="dividend-wallet-save"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white px-5 py-2 text-sm font-semibold transition disabled:opacity-60"
          >
            <Save size={14} /> {saving ? 'Saving...' : 'Save Wallet'}
          </button>
        </div>
      </form>

      {/* Transaction history */}
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Transaction History</span>
        </div>

        {items.length === 0 ? (
          <div className="text-emerald-900/50 italic text-[13px] py-6 text-center">No dividend transactions yet. Once admin distributes dividends, they will appear here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="dividend-history" className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-widest text-emerald-900/60 border-b border-emerald-900/15">
                  <th className="text-left py-2 pr-3">TX ID</th>
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Project</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-left py-2 pr-3">Type</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2">TX Hash</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-emerald-900/10 hover:bg-white/40">
                    <td className="py-2 pr-3 font-mono text-emerald-900">{it.tx_id}</td>
                    <td className="py-2 pr-3 text-emerald-900/80">{new Date(it.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td className="py-2 pr-3 text-emerald-900/80">{it.project}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-emerald-950">{money(it.amount)}</td>
                    <td className="py-2 pr-3 text-emerald-800">Dividend</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${it.status === 'PAID' ? 'bg-emerald-500 text-emerald-950' : 'bg-amber-200 text-amber-800'}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="py-2 font-mono text-[11px] text-emerald-800/70 truncate max-w-[220px]">{it.tx_hash || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, highlight }) => (
  <div className={`rounded-2xl border p-5 ${highlight ? 'border-emerald-500/40 bg-emerald-50' : 'border-emerald-900/15 bg-[#fdf9e7]'}`}>
    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-800/70">{label}</div>
    <div className="mt-1 font-serif text-[28px] text-emerald-950">{value}</div>
    {sub && <div className="text-[12px] text-emerald-800/60 mt-0.5">{sub}</div>}
  </div>
);

export default DividendTab;
